import Vue from 'vue'
import VueRouter from 'vue-router'
import * as Sentry from '@sentry/browser'
import * as Integrations from '@sentry/integrations'
import { Integrations as Tracing } from '@sentry/tracing'
import router from '@/router'
import { CompanyAPI, UserAPI } from '@/api/backend-client'

if (process.env.VUE_APP_SENTRY_DSN) {
    Sentry.init({
        dsn: process.env.VUE_APP_SENTRY_DSN,
        integrations: [
            // attachProps: false - component props can hold contact and lead data, and Sentry is hosted in the US
            new Integrations.Vue({ Vue, attachProps: false }),
            new Tracing.BrowserTracing({
                // Start page loads from vue-router and name them after the route, so /tasks/<id> counts as one
                // page instead of one per id. The default instrumentation reads the URL before it changes,
                // which reports every navigation under the previous page.
                routingInstrumentation: startTransaction => {
                    router.afterEach((to, from) => {
                        // Public pages (login, password reset, consumer forms and widgets) have tokens and ids in
                        // the URL and no user to count. Same check as the auth guard in router/index.ts.
                        if (to.meta.anonymous === true) {
                            return
                        }

                        startTransaction({
                            name: to.name || to.path,
                            op: from.matched.length === 0 ? 'pageload' : 'navigation',
                        })
                    })
                },

                // No spans for API requests: their URLs carry list filters such as the search term
                traceXHR: false,
                traceFetch: false,
            }),
        ],

        release: process.env.VUE_APP_VERSION,
        environment: process.env.VUE_APP_SENTRY_ENVIRONMENT,

        // Page usage tracking. 25% keeps the org within its span quota; Sentry scales counts back up.
        tracesSampleRate: 0.25,

        // A browser notice, not an error (thousands of events a month)
        ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
        ],

        // vue-router 3 rejects router.push() when a guard stops or redirects a navigation on purpose (Beta users go
        // to the One app, logged-out users to the login page) or when the target is the current page. Not an error.
        // vue-router 3.x has isNavigationFailure as a static on the class, not as a named export.
        beforeSend(event, hint) {
            return hint && VueRouter.isNavigationFailure(hint.originalException) ? null : event
        },

        // Console breadcrumbs carry what the app logs, e.g. a failed request's body with names and e-mail addresses
        beforeBreadcrumb(breadcrumb) {
            return breadcrumb.category === 'console' ? null : breadcrumb
        },
    })

    // A transaction only needs its route name. Drop what carries URLs: breadcrumbs (API calls with their filters,
    // navigations), the page URL and referrer, and resource spans (also most of the span volume). beforeSend does
    // not run for transactions, and global processors run after the scope has added the breadcrumbs.
    Sentry.addGlobalEventProcessor(event => {
        if (event.type !== 'transaction') {
            return event
        }

        delete event.breadcrumbs
        if (event.request) {
            delete event.request.url
            if (event.request.headers) {
                delete event.request.headers.Referer
            }
        }
        event.spans = event.spans?.filter(span => !span.op?.startsWith('resource'))

        return event
    })

    // Attach who is using the page, so usage can be counted per user and per company.
    router.afterEach(() => {
        const user = UserAPI.currentUser
        const company = CompanyAPI.currentCompany

        Sentry.configureScope(scope => {
            scope.setUser(user ? { id: user.id } : null)
            scope.setTag('company', company?.name)
            scope.setTag('super_admin', user ? String(user.superAdmin) : undefined)
        })
    })
}
